import { CompanySelect } from './components/dashboard/CompanySelect';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, RefreshCw } from 'lucide-react';

function getCurrentMonthRange() {
  const today = new Date();
  const dataInicial = new Date(today.getFullYear(), today.getMonth(), 1);
  const dataFinal = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  return { dataInicial: formatDate(dataInicial), dataFinal: formatDate(dataFinal) };
}

export default function ContaPagarDetalhe({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestRef = useRef(null);

  // Estados dos Filtros de Requisição (Backend)
  const [filtroDataInicial, setFiltroDataInicial] = useState(() => getCurrentMonthRange().dataInicial);
  const [filtroDataFinal, setFiltroDataFinal] = useState(() => getCurrentMonthRange().dataFinal);
  const [filtroEmpresa, setFiltroEmpresa] = useState('');

  // Estados dos Filtros Locais (Grid)
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [selectedFornecedor, setSelectedFornecedor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedForma, setSelectedForma] = useState('');
  const [selectedNomeConta, setSelectedNomeConta] = useState('');
  const [selectedSequencial, setSelectedSequencial] = useState('');
  const [selectedDocumento, setSelectedDocumento] = useState('');

  const base_url = process.env.REACT_APP_API_URL || 'http://localhost:5008';

  const carregarDetalhes = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    setError('');
    setItems([]);
    const token = localStorage.getItem('@user_token');
    if (!token) {
      setError('Faça login para consultar os pagamentos.');
      setLoading(false);
      return;
    }
    if (filtroDataInicial && filtroDataFinal && filtroDataInicial > filtroDataFinal) {
      setError('A data inicial não pode ser posterior à data final.');
      setLoading(false);
      return;
    }
    const params = new URLSearchParams();
    if (filtroDataInicial) params.set('dataInicial', filtroDataInicial);
    if (filtroDataFinal) params.set('dataFinal', filtroDataFinal);
    if (filtroEmpresa) params.set('empresa', filtroEmpresa);
    try {
      const response = await fetch(`${base_url.replace(/\/$/, '')}/api/dashboard/financeiro/ContaPagarDetalhe?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' },
        signal: controller.signal,
      });
      if (!response.ok) {
        const messages = {
          400: 'Verifique o período e a empresa informados.',
          401: 'Sua sessão expirou. Faça login novamente.',
          403: 'Você não tem permissão para consultar os pagamentos.',
          429: 'Muitas consultas. Aguarde e tente novamente.',
        };
        throw new Error(messages[response.status] || 'Não foi possível consultar os pagamentos.');
      }
      const json = response.status === 204 ? [] : await response.json();
      if (!controller.signal.aborted) setItems(Array.isArray(json) ? json : (json ? [json] : []));
    } catch (err) {
      if (!controller.signal.aborted) {
        setItems([]);
        setError(err instanceof TypeError ? 'Não foi possível acessar a API. Verifique sua conexão.' : err.message);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [base_url, filtroDataInicial, filtroDataFinal, filtroEmpresa]);

  useEffect(() => {
    carregarDetalhes();
    return () => requestRef.current?.abort();
  }, [carregarDetalhes]);
  const formatBRL = (val) => 
    Number(val || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDataBR = (dataString) => {
    if (!dataString) return '-';
    if (typeof dataString === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dataString)) return dataString;

    try {
      const dataObj = new Date(dataString);
      if (isNaN(dataObj.getTime())) return '-';
      
      const dia = String(dataObj.getUTCDate()).padStart(2, '0');
      const mes = String(dataObj.getUTCMonth() + 1).padStart(2, '0');
      const ano = dataObj.getUTCFullYear();
      
      return `${dia}/${mes}/${ano}`;
    } catch {
      return '-';
    }
  };

  const getValue = (obj, key) => {
    if (!obj) return '';
    const aliases = {
      Fornecedor: ['FORNECEDOR', 'Fornecedor', 'fornecedor'],
      DataVencimento: ['Data Vencimento', 'VENCIMENTO', 'DataVencimento', 'dataVencimento'],
      Quitacao: ['Quitação', 'Data Quitação', 'QUITAÇÃO', 'Quitacao', 'quitacao'],
      FormaCobranca: ['Forma de Cobrança', 'FORMA COBRANÇA', 'FormaCobranca', 'formaCobranca'],
      NomeConta: ['Nome Conta', 'NomeConta', 'nomeConta'],
      Pago: ['Valor Pago', 'ValorPago', 'valorPago', 'PAGO', 'Pago', 'pago'],
      Desconto: ['Valor Desconto', 'DESCONTO', 'Desconto', 'desconto'],
      acrescimo: ['Acréscimo', 'Valor Acréscimo', 'ACRESCIMO', 'Acrescimo', 'acrescimo'],
    };
    const names = aliases[key] || [key.toUpperCase(), key, key.charAt(0).toLowerCase() + key.slice(1)];
    const name = names.find(candidate => obj[candidate] !== undefined);
    return name === undefined ? '' : obj[name];
  };
  const getUniqueValues = (key) => {
    return [...new Set(items.map(item => getValue(item, key)))]
      .filter(val => val !== null && val !== undefined && val !== '')
      .sort();
  };

  const itemsFiltrados = items.filter(item => {
    if (Number(getValue(item, 'Valor') || 0) === 0) return false;
    if (selectedStatus === 'Quitado' && getValue(item, 'Status') === 'Quitado' && Number(getValue(item, 'Pago') || 0) === 0) {
      return false;
    }
    return (
      (selectedEmpresa === '' || String(getValue(item, 'Empresa')) === selectedEmpresa) &&
      (selectedFornecedor === '' || String(getValue(item, 'Fornecedor')) === selectedFornecedor) &&
      (selectedStatus === '' || String(getValue(item, 'Status')) === selectedStatus) &&
      (selectedForma === '' || String(getValue(item, 'FormaCobranca')) === selectedForma) &&
      (selectedNomeConta === '' || String(getValue(item, 'NomeConta')) === selectedNomeConta) &&
      (selectedSequencial === '' || String(getValue(item, 'Sequencial')) === selectedSequencial) &&
      (selectedDocumento === '' || String(getValue(item, 'Documento')) === selectedDocumento)
    );
  });

  const totalValor = itemsFiltrados.reduce((acc, item) => {
    if (selectedStatus === '') return acc + Number(getValue(item, 'Valor') || 0);

    const pago = Number(getValue(item, 'Pago') || 0);
 
    if (getValue(item, 'Status') === 'Quitado') 
      return acc + pago;
    const desconto = Number(getValue(item, 'Desconto') || 0);
    const acrescimo = Number(getValue(item, 'acrescimo') || 0);
     
    return acc + pago + desconto - acrescimo;
  }, 0); 
  
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#0d1117] border border-gray-800 w-full max-w-7xl h-[90vh] rounded-lg shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Cabeçalho */}
        <div className="flex justify-between items-center p-4 border-b border-gray-800 bg-[#161b22]">
          <div className="flex items-center gap-2 text-yellow-500 font-bold tracking-widest text-xs uppercase">
            <span>Detalhamento Avançado de Contas a Pagar</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Barra de Filtros API */}
        <div className="p-4 bg-[#161b22]/50 border-b border-gray-800 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label htmlFor="data-inicial-pagamentos" className="text-gray-500 text-[10px] uppercase font-bold block mb-1">Data inicial</label>
            <input id="data-inicial-pagamentos" type="date" value={filtroDataInicial} onChange={(e) => setFiltroDataInicial(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded px-2 py-1 text-sm text-white font-mono" />
          </div>
          <div>
            <label htmlFor="data-final-pagamentos" className="text-gray-500 text-[10px] uppercase font-bold block mb-1">Data final</label>
            <input id="data-final-pagamentos" type="date" value={filtroDataFinal} onChange={(e) => setFiltroDataFinal(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded px-2 py-1 text-sm text-white font-mono" />
          </div>
          <div>
            <label htmlFor="empresa-api-pagamentos" className="text-gray-500 text-[10px] uppercase font-bold block mb-1">Empresa (API)</label>
            <CompanySelect id="empresa-api-pagamentos" label={false} value={filtroEmpresa} onChange={setFiltroEmpresa} apiBaseUrl={base_url} allLabel="Todas" className="w-full bg-[#0d1117] border border-gray-800 rounded px-2 py-1 text-sm text-white" />
          </div>
          <button onClick={carregarDetalhes} className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-[10px] font-black h-8 px-4 rounded transition-all uppercase tracking-widest shadow-lg">
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>

        {/* Grid de Filtros Locais */}
        <div className="p-4 bg-[#161b22] border-b border-gray-800 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-xs">
          <div>
            <select value={selectedEmpresa} onChange={(e) => setSelectedEmpresa(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Empresa</option>
              {getUniqueValues('Empresa').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          <div>
            <select value={selectedSequencial} onChange={(e) => setSelectedSequencial(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Sequencial</option>
              {getUniqueValues('Sequencial').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          <div>
            <select value={selectedDocumento} onChange={(e) => setSelectedDocumento(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Documento</option>
              {getUniqueValues('Documento').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          <div>
            <select aria-label="Filtrar fornecedor" value={selectedFornecedor} onChange={(e) => setSelectedFornecedor(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Fornecedor</option>
              {getUniqueValues('Fornecedor').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          <div>
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Status</option>
              {getUniqueValues('Status').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          
          <div>
            <select value={selectedForma} onChange={(e) => setSelectedForma(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Forma Cob.</option>
              {getUniqueValues('FormaCobranca').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
          <div>
            <select value={selectedNomeConta} onChange={(e) => setSelectedNomeConta(e.target.value)} className="w-full bg-[#0d1117] border border-gray-800 rounded p-1 text-gray-300">
              <option value="">Nome Conta</option>
              {getUniqueValues('NomeConta').map(val => <option key={val} value={val}>{val}</option>)}
            </select>
          </div>
        </div>

        {/* Tabela de Dados */}
        <div className="flex-1 overflow-auto p-4 bg-[#0d1117]">
          {loading ? (
            <div className="w-full flex items-center justify-center h-48 text-yellow-500 font-bold animate-pulse uppercase tracking-widest text-xs">
              Carregando registros detalhados...
            </div>
          ) : error ? (<div role="alert" className="p-4 text-red-400">{error}</div>) : itemsFiltrados.length === 0 ? (
            <div className="w-full flex items-center justify-center h-48 text-gray-600 font-bold uppercase tracking-widest text-xs">
              Nenhum registro encontrado para os filtros atuais.
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-800 rounded">
              <table className="min-w-full divide-y divide-gray-800 text-left text-xs">
                <thead className="bg-[#161b22] text-gray-400 uppercase font-black tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-3">Empresa</th>
                    <th className="px-3 py-3 text-center">Seq.</th>
                    <th className="px-3 py-3">Documento</th>
                    <th className="px-3 py-3">Fornecedor</th>
                    <th className="px-3 py-3 text-center">Data Venc.</th>
                    <th className="px-3 py-3 text-center">Quitação</th>
                    <th className="px-3 py-3 text-center">Status</th>
                   {/*<th className="px-3 py-3 text-right">Valor</th><th className="px-3 py-3 text-right">Pago</th>*/}
                    <th className="px-3 py-3 text-right">Valor</th>
                    <th className="px-3 py-3 text-right">Desconto</th>
                    <th className="px-3 py-3 text-right">Acréscimo</th>
                    <th className="px-3 py-3 text-right">Pago</th>
                    <th className="px-3 py-3">Forma Cobr.</th>
                    <th className="px-3 py-3">Nome Conta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800 bg-[#0d1117] text-gray-300 font-medium">
                  {itemsFiltrados.map((item, index) => {
                    const statusVal = getValue(item, 'Status');
                    const dataVenc = getValue(item, 'DataVencimento');
                    const dataQuit = getValue(item, 'Quitacao');

                    return (
                      <tr key={index} className="hover:bg-[#161b22]/40 transition-colors">
                        <td className="px-3 py-3 whitespace-nowrap font-bold text-white">{getValue(item, 'Empresa')}</td>
                        <td className="px-3 py-3 text-center font-mono text-gray-400">{getValue(item, 'Sequencial')}</td>
                        <td className="px-3 py-3 font-mono text-gray-300 whitespace-nowrap">{getValue(item, 'Documento') || '-'}</td>
                        <td className="px-3 py-3 truncate max-w-[180px]" title={getValue(item, 'Fornecedor')}>{getValue(item, 'Fornecedor')}</td>
                        <td className="px-3 py-3 text-center font-mono whitespace-nowrap">{formatDataBR(dataVenc)}</td>
                        <td className="px-3 py-3 text-center font-mono whitespace-nowrap">{formatDataBR(dataQuit)}</td>
                        <td className="px-3 py-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${statusVal === 'Quitado' ? 'bg-green-950 text-green-400 border border-green-800' : 'bg-yellow-950 text-yellow-400 border border-yellow-800'}`}>
                            {statusVal || 'Pendente'}
                          </span>
                        </td>
                       {/* <td className="px-3 py-3 text-right font-mono font-bold text-white">{formatBRL(getValue(item, 'Valor'))}</td>*/}
                        <td className="px-3 py-3 text-right font-mono text-white">{formatBRL(getValue(item, 'Valor'))}</td>
                        <td className="px-3 py-3 text-right font-mono text-gray-300">{formatBRL(getValue(item, 'Desconto'))}</td>
                        <td className="px-3 py-3 text-right font-mono text-gray-300">{formatBRL(getValue(item, 'acrescimo'))}</td>
                        <td className="px-3 py-3 text-right font-mono text-emerald-400">{formatBRL(getValue(item, 'Pago'))}</td>
                        <td className="px-3 py-3 whitespace-nowrap text-gray-400">{getValue(item, 'FormaCobranca') || '-'}</td>
                        <td className="px-3 py-3 truncate max-w-[150px]" title={getValue(item, 'NomeConta')}>{getValue(item, 'NomeConta') || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Rodapé Alinhado com a Coluna do Grid */}
        <div className="p-4 bg-[#161b22] border-t border-gray-800 grid grid-cols-3 items-center text-[10px] uppercase font-bold tracking-wider text-gray-500">
          {/* Esquerda: Contador de registros */}
          <div className="text-left">
            Registros exibidos: <span className="text-white font-mono">{itemsFiltrados.length}</span> de <span className="text-gray-400 font-mono">{items.length}</span>
          </div>

          {/* Centro: Botão de ação */}
          <div className="text-center">
            <button onClick={onClose} className="bg-gray-800 hover:bg-gray-700 text-white text-[10px] font-black py-1.5 px-6 rounded transition-all shadow-lg uppercase tracking-widest">
              Fechar Janela
            </button>
          </div>

          {/* Direita: Total do valor dos registros exibidos */}
          <div className="text-right">
            Total: <span className="text-emerald-400 font-mono text-xs font-black ml-1">{loading || error ? '-' : formatBRL(totalValor)}</span>
             
          </div>
          
        </div>

      </div>
    </div>
  );
}
